import { TonemappingMode } from "@novorender/api";
import { Box, Checkbox, Select, spacing } from "@procore/core-react";
import type { quat, vec3 } from "gl-matrix";
import type { Webviewer } from "lib";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { type CameraState, CameraType, renderActions, selectAdvanced } from "features/render";
import type { SetViewpointPayload } from "features/viewpoints/types";

import { CopyButton, PasteButton, Section } from "./common";

export function ViewSection() {
    const {
        state: { webviewer },
    } = useExplorerGlobals(true);
    const dispatch = useAppDispatch();

    return (
        <Section title="View">
            <Box style={{ display: "flex", gap: spacing.xs }}>
                <CopyButton
                    label="viewpoint"
                    disabled={!webviewer}
                    getText={async () =>
                        JSON.stringify(
                            await webviewer!.model.getViewpoint({
                                createSnapshot: false,
                                collapseVisibilityExceptions: true,
                            }),
                            null,
                            2,
                        )
                    }
                >
                    Copy viewpoint
                </CopyButton>
                <PasteButton
                    label="viewpoint"
                    disabled={!webviewer}
                    apply={(text) => webviewer!.model.setViewpoint(JSON.parse(text) as SetViewpointPayload)}
                />
            </Box>
            <Box style={{ display: "flex", gap: spacing.xs }}>
                <CopyButton label="camera" disabled={!webviewer} getText={() => getCameraJson(webviewer!)}>
                    Copy camera
                </CopyButton>
                <PasteButton label="camera" apply={(text) => dispatch(renderActions.setCamera(parseCamera(text)))} />
            </Box>
            <RenderDebugControls />
        </Section>
    );
}

const TONEMAPPING_MODES = Object.entries(TonemappingMode).filter(
    (entry): entry is [string, TonemappingMode] => typeof entry[1] === "number",
);

/** Goes through the advanced render settings, which would otherwise overwrite a direct `view` change. */
function RenderDebugControls() {
    const dispatch = useAppDispatch();
    const { debug, tonemapping } = useAppSelector(selectAdvanced);

    return (
        <Box
            style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                rowGap: spacing.sm,
                columnGap: spacing.lg + spacing.xs,
            }}
        >
            <Box style={{ display: "flex", columnGap: spacing.lg }}>
                {/* Separate boxes: core-react adds a top margin to adjacent checkboxes. */}
                <Box>
                    <Checkbox
                        checked={debug.wireframe}
                        onChange={(e) =>
                            dispatch(renderActions.setAdvanced({ debug: { wireframe: e.target.checked } }))
                        }
                    >
                        Wireframe
                    </Checkbox>
                </Box>
                <Box>
                    <Checkbox
                        checked={debug.showNodeBounds}
                        onChange={(e) =>
                            dispatch(renderActions.setAdvanced({ debug: { showNodeBounds: e.target.checked } }))
                        }
                    >
                        Show node bounds
                    </Checkbox>
                </Box>
            </Box>
            <Select
                label={TonemappingMode[tonemapping.mode]}
                onSelect={({ item }) =>
                    dispatch(renderActions.setAdvanced({ tonemapping: { mode: item as TonemappingMode } }))
                }
                aria-label="Tonemapping mode"
            >
                {TONEMAPPING_MODES.map(([name, mode]) => (
                    <Select.Option key={mode} value={mode} selected={mode === tonemapping.mode}>
                        {name}
                    </Select.Option>
                ))}
            </Select>
        </Box>
    );
}

function getCameraJson(webviewer: Webviewer) {
    const { kind, position, rotation, fov } = webviewer.camera.getCamera();
    return JSON.stringify({ kind, position: Array.from(position), rotation: Array.from(rotation), fov });
}

/** @throws Error If `text` is not camera JSON as produced by {@link getCameraJson}. */
function parseCamera(text: string): CameraState {
    const { kind, position, rotation, fov } = JSON.parse(text) as {
        kind?: unknown;
        position?: unknown;
        rotation?: unknown;
        fov?: unknown;
    };
    const isNumbers = (value: unknown, length: number): value is number[] =>
        Array.isArray(value) && value.length === length && value.every((n) => typeof n === "number");
    if (!isNumbers(position, 3) || !isNumbers(rotation, 4) || typeof fov !== "number") {
        throw new Error("Clipboard does not hold a camera");
    }
    const goTo = { position: position as vec3, rotation: rotation as quat, fov };
    return kind === "orthographic" ? { type: CameraType.Orthographic, goTo } : { type: CameraType.Pinhole, goTo };
}
